// src/pages/WatchFindings.jsx
// E5a-2 — die Liste des nächtlichen Preis-Prüfers.
//
// WOZU DIESE SEITE EXISTIERT
// --------------------------
// E5a-1 hat die Befunde erzeugt und über einen Endpunkt erreichbar gemacht.
// **Ohne diese Seite wäre das `needs_selector_refresh` noch einmal** — ein
// Signal, das seit sechs Wochen niemanden erreicht, weil es keine Fläche hat.
// Genau der Fehler, den der Prüfer repariert.
//
// 🔴 WAS DIESE LISTE VON JEDER ANDEREN UNTERSCHEIDET
// --------------------------------------------------
// Eine leere Liste heisst hier **nicht** „alles in Ordnung". Sie heisst nur
// dann etwas, wenn der Lauf auch stattgefunden hat — und der Prüfer
// überspringt sich, wenn der Scrape noch läuft. Deshalb steht der **Lauf-Status
// über der Liste, nicht darunter**, und er ist die einzige Karte, die auch bei
// null Befunden erscheint.
//
// Aufbau nach RedirectReviewInbox.jsx (PageContainer + ViewHeader + Tokens),
// Ladezustand über Skeletons statt Spinner (Repo-Regel).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import PageContainer from '../components/common/PageContainer';
import ViewHeader from '../components/ViewHeader';
import EmptyState from '../components/common/EmptyState';
import { listWatchFindings, setWatchFindingState } from '../api/watchFindings';
import { PANEL_SX, SP, FS } from '../theme/tokens';

const ZUSTAENDE = ['open', 'done', 'dismissed'];

const ZUSTAND_LABEL = {
  open: 'Open',
  done: 'Done',
  dismissed: 'Dismissed',
};

// 🔴 Die Klassen in Worten, nicht als Aufzählungswert — dieselbe Übersetzung
// wie bei den Selektor-Befunden, aus demselben Grund: `stored_unreproducible`
// ist Maschinenvokabular.
const KLASSE = {
  disagreement: {
    label: 'Techniques disagree',
    farbe: 'v2.warn.main',
    erklaerung: 'Two techniques read different prices on the same page. '
      + 'The only class that finds a silently wrong price.',
  },
  stored_unreproducible: {
    label: 'Stored price not reproducible',
    farbe: 'v2.warn.main',
    erklaerung: 'The techniques agree — but the stored price is '
      + 'none of them, and neither a cart discount nor VAT '
      + 'explains that.',
  },
  price_jump: {
    label: 'Price jump',
    farbe: 'text.primary',
    erklaerung: 'The price deviates from the previous day beyond the threshold. Can be a '
      + 'real promotion — so this class ranks behind the others.',
  },
};

/**
 * 🔴 `de-DE`, nicht `toFixed`. Die erste Fassung schrieb `6.29 €` mitten in eine
 * Seite, die ihre Daten mit `toLocaleString('de-DE')` schreibt — und meine eigene
 * Commit-Meldung schrieb daneben „6,29 €" mit Komma. Repo-Kanon ist
 * `Intl.NumberFormat` (12+ Dateien, u.a. `ScraperConfigForm.jsx:109`).
 * Vom Regel-16-Gate gefunden.
 */
const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function preis(v) {
  const n = Number(v);
  return Number.isFinite(n) ? EUR.format(n) : '–';
}

/**
 * Der Lauf-Status. 🔴 Steht ÜBER der Liste und erscheint auch bei null Befunden.
 *
 * Ohne ihn liest sich eine leere Liste wie „alles in Ordnung". Sie kann aber
 * genauso heissen: der Lauf wurde übersprungen, weil der Scrape noch lief; oder
 * er lief, und 180 Ziele waren gar nicht messbar.
 */
function LaufKarte({ lauf }) {
  if (!lauf) {
    return (
      <Alert severity="warning" sx={{ mb: SP.section }}>
        <strong>There is no run.</strong> An empty list therefore means
        nothing here — the checker has never reported.
      </Alert>
    );
  }
  const s = lauf.summary || {};
  const uebersprungen = s.skipped === true;
  const fehler = lauf.status === 'error';

  return (
    <Alert
      severity={fehler ? 'error' : uebersprungen ? 'warning' : 'info'}
      sx={{ mb: SP.section }}
    >
      <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
        {fehler
          ? 'The last run failed.'
          : uebersprungen
            ? 'The last run was skipped — nothing was measured.'
            : `Last run: ${s.targets ?? '?'} targets checked.`}
      </Typography>

      {uebersprungen && (
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          Reason: {s.reason === 'scrape_still_running'
            ? 'the nightly scrape was still running.'
            : s.reason || 'unknown'}
        </Typography>
      )}
      {fehler && lauf.last_error && (
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          {lauf.last_error}
        </Typography>
      )}

      {/* 🔴 Die Zahlen, ohne die „0 Widersprüche" wie „alles in Ordnung" liest. */}
      {!uebersprungen && !fehler && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip size="small" label={`not comparable: ${s.not_comparable ?? 0}`} />
          <Chip size="small" label={`no previous-day price: ${s.missing_stored_yesterday ?? 0}`} />
          {/* 🔴 KEIN `color="warning"`. Der gefuellte Warn-Chip misst gemessen
              3,11:1 (weiss auf #ed6c02, 11,4 px) — unter WCAG 1.4.3, und zwar
              GENAU DANN, wenn die Zahl ungleich null ist, also genau dann, wenn
              sie gelesen werden muss. Vom Regel-16-Gate im laufenden Browser
              gemessen.
              Den Unterschied traegt jetzt die SPRACHE statt der Farbe: „scrape
              missing" heisst etwas anderes als „no previous-day price", und das
              lernt auch jemand, der wochenlang nur Nullen sieht. */}
          <Chip
            size="small"
            variant={(s.missing_stored_today ?? 0) > 0 ? 'filled' : 'outlined'}
            label={`scrape missing: ${s.missing_stored_today ?? 0}`}
          />
          {typeof s.threshold_pct === 'number' && (
            <Chip size="small" variant="outlined" label={`threshold ${s.threshold_pct} %`} />
          )}
        </Stack>
      )}

      {(s.missing_stored_today ?? 0) > 0 && (
        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
          “No price from today” means: the scrape wrote nothing for these
          targets, while the checker could read the page — a silent
          scrape failure, not a price problem.
        </Typography>
      )}

      {lauf.finished_at && (
        <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.8 }}>
          {new Date(lauf.finished_at).toLocaleString('de-DE')}
        </Typography>
      )}
    </Alert>
  );
}

/**
 * E5b — aus Befunden werden Konfigurationsentscheidungen.
 *
 * 🔴 **Ohne Modell, und das ist gemessen.** Der Regel-14-Grill hat den
 * modellgestützten Zuschnitt verworfen: an den 14 echten Befunden lösen sich
 * **12 von 14** allein über das historische Band auf, und in **11 von 14** ist
 * die Antwort bereits der gespeicherte Preis.
 *
 * Und die Befunde sind gar keine Preisrätsel — sie sind kaputte Selektoren:
 * `knives4life`/`html` liest viermal 1,00 €, `Aromatico`/`jsonld` viermal einen
 * Bruchteil. **14 Befunde sind fünf Konfigurationen** plus zwei Fälle für einen
 * Menschen, und für die Konfigurationen gibt es E3 und E4 bereits.
 *
 * 🔴 Hier stand „10 von 13", „dreimal", „13 Befunde" und „sechs" — vier Zahlen,
 * alle aus einem Gate-Bericht übernommen statt gerechnet. Gegen Prod nachgezählt.
 */
function Diagnose({ diagnose, onGenerator }) {
  if (!diagnose) return null;
  if (diagnose.error) {
    return (
      <Alert severity="warning" sx={{ mb: SP.section }}>
        The diagnosis could not be computed ({diagnose.error}). The findings below
        still hold — only the summary is missing.
      </Alert>
    );
  }
  const {
    vorschlaege = [], gespeichert_unplausibel: unplausibel = [],
    ohne_erklaerung: unerklaert = [], ohne_band = 0,
    schwelle_pct: schwelle = 20,
  } = diagnose;

  // 🔴 NICHTS zu rendern ist hier keine Option. Genau diese Falle steht im Kopf
  // dieser Datei eine Ebene tiefer beschrieben: eine leere Fläche liest sich wie
  // „alles in Ordnung". Es gibt Befunde — die Diagnose hat nur keinen davon
  // erklären können, und das ist eine Aussage, kein Nichts.
  if (!vorschlaege.length && !unplausibel.length && !unerklaert.length && !ohne_band) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: SP.section }}>
        There is no diagnosis for the findings below — none of them deviates by
        more than {schwelle} % from the median of the last 90 days. That does not
        mean they are fine: a permanently wrong price has no deviation.
      </Typography>
    );
  }

  return (
    <Box sx={{ mb: SP.section }}>
      {/* 🔴 ZUERST das, was JETZT etwas kostet: nicht ein Selektor liest schief,
          sondern der Wert steht bereits in der Preisautomatik. */}
      {/* 🔴 Nur der ERSTE meldet sich assertiv. Sechs `role="alert"` gleichzeitig
          sind für einen Screenreader keine Warnung, sondern eine Wand — dieselbe
          Deckelung wie in `NoticeSlot`, nur ohne die Einträge zu verstecken:
          jeder kostet Geld, keiner darf eingeklappt werden. */}
      {unplausibel.map((g, i) => (
        <Alert severity="error" key={g.finding_id} sx={{ mb: SP.card }}
               role={i === 0 ? 'alert' : 'status'}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {g.competitor_name} · {g.sku}: stored {preis(g.gespeichert)} — the
            median of the last 90 days is {preis(g.median)}.
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            This value is already in the price automation. {g.beobachtungen} observations,
            spread {g.spanne_pct} %.
            {/* Die Schwelle kommt vom Server. Hier stand eine feste 20 — zwei
                Bedeutungen auf einer Ziffer, und die eine wandert mit. */}
            {g.spanne_pct > schwelle && ' The target scatters widely — the median is a weaker reference here.'}
          </Typography>
        </Alert>
      ))}

      {vorschlaege.length > 0 && (
        <Card sx={PANEL_SX}>
          <CardContent>
            {/* Kein `component="h3"`: gemessen stand die Seite auf H5 → H3 → H6,
                also rangierte dieser Kasten über der Seitenüberschrift. Die
                Nachbarkarte macht es ohne `component` richtig. */}
            <Typography variant="subtitle2">
              What to do — {vorschlaege.length} configuration{vorschlaege.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
              The findings below are not price puzzles: they show which technique is off for
              which competitor. Deviation of more than {diagnose.schwelle_pct} % from the
              median of the last 90 days.
            </Typography>
            {vorschlaege.map((v) => (
              <Stack key={`${v.competitor_id}-${v.technique}`} direction="row"
                     sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.75,
                           borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.competitor_name}</Typography>
                <Chip size="small" label={v.technique} />
                <Typography variant="body2">
                  deviates for {v.ziele} target{v.ziele !== 1 ? 's' : ''}
                </Typography>
                {v.beispiele[0] && (
                  <Typography variant="caption" color="text.secondary">
                    e.g. reads {preis(v.beispiele[0].gelesen)} instead of {preis(v.beispiele[0].median)}
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {/* Der Weg, den der Endstadium-Plan nennt: „von dort ein Knopf
                    direkt in E3".
                    🔴 Die Beschriftung hiess „Konfiguration ansehen" und
                    versprach mehr, als der Weg hält: das Regel-16-Gate ist ihn
                    gegangen (Aromatico) und hat gemessen, dass auf der Zielseite
                    weder die Technik noch der Befund auftaucht. Bis der
                    Zusammenhang mitfährt, sagt der Knopf, was er tut. Die
                    Technik steht in der Zeile daneben, damit man weiss, wonach
                    man dort sucht.
                    Nachgehalten in
                    `docs/backlog-auto-repair/2026-08-16-der-befund-faehrt-nicht-in-den-generator-mit.md`
                    — der Kommentar sagte „nachgehalten im Backlog", und die Datei
                    gab es nicht. Von der Wiedervorlage des Regel-16-Gates
                    gefunden, das den Kommentar gegen das Verzeichnis geprueft
                    hat statt ihn zu glauben. */}
                <Button size="small" variant="outlined"
                        onClick={() => onGenerator(v.competitor_id)}>
                  Open competitor
                </Button>
              </Stack>
            ))}
          </CardContent>
        </Card>
      )}

      {unerklaert.length > 0 && (
        <Typography variant="body2" color="text.secondary" component="p" sx={{ mt: 1 }}>
          For {unerklaert.length} finding{unerklaert.length !== 1 ? 's' : ''} <strong>every</strong>
          read number deviates from the median. This is not a broken
          selector — either the price really moved, or all techniques
          are off. Either way a human must look:
        </Typography>
      )}
      {/* Eine LISTE, kein Fliesstext, und gedeckelt. Bei zehn Eintraegen war die
          Aneinanderreihung 431 Zeichen am Stueck — lesbar nur, wenn man sie
          nicht lesen muss. Vom Regel-16-Gate als gleiche Risikoklasse wie die
          bereits angenommene W-3 benannt; hier gleich mitgenommen, weil es zwei
          Zeilen sind. */}
      {unerklaert.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
          {unerklaert.slice(0, 5).map((u) => (
            <Typography component="li" variant="body2" color="text.secondary"
                        key={u.finding_id}>
              {u.competitor_name} · {u.sku} — Median {preis(u.median)}
              {u.techniken?.length ? ` (${u.techniken.join(', ')})` : ''}
            </Typography>
          ))}
          {unerklaert.length > 5 && (
            <Typography component="li" variant="body2" color="text.secondary">
              and {unerklaert.length - 5} more
            </Typography>
          )}
        </Box>
      )}

      {ohne_band > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {ohne_band} finding{ohne_band !== 1 ? 's' : ''} without enough history — there
          is no verdict there, only the finding itself.
        </Typography>
      )}
    </Box>
  );
}

function BefundKarte({ befund, busy, onZustand }) {
  const k = KLASSE[befund.kind] || { label: befund.kind, farbe: 'text.primary', erklaerung: null };
  const d = befund.detail || {};
  const alle = d.all_classes || [];

  return (
    <Card sx={{ ...PANEL_SX, mb: SP.card }}>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ color: k.farbe }}>{k.label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {befund.competitor_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">{befund.sku}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {befund.found_on
              ? new Date(`${befund.found_on}T00:00:00`).toLocaleDateString('de-DE')
              : ''}
          </Typography>
        </Stack>

        {k.erklaerung && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            {k.erklaerung}
          </Typography>
        )}

        {/* 🔴 Mehr als eine Klasse ausgelöst? Dann steht das DA. Sonst ist ein
            Ziel mit zwei Befunden von einem mit einem nicht zu unterscheiden. */}
        {alle.length > 1 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {alle.map((c) => (
              <Chip key={c} size="small" variant="outlined"
                    label={KLASSE[c]?.label || c} />
            ))}
          </Stack>
        )}

        {/* Der GRUND, nicht nur das Urteil. */}
        <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Stored today
            </Typography>
            <Typography variant="body2">{preis(d.stored_today)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Stored yesterday
            </Typography>
            <Typography variant="body2">{preis(d.stored_yesterday)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              Check run
            </Typography>
            <Typography variant="body2">
              {(d.probe_prices || []).length
                ? d.probe_prices.map((p) => preis(p)).join('  /  ')
                : 'no price'}
            </Typography>
          </Box>
        </Stack>

        {Array.isArray(d.candidates) && d.candidates.length > 0 && (
          <Box component="table" sx={{ mt: 1.5, borderCollapse: 'collapse' }}>
            {/* 🔴 SICHTBARE Kopfzeile. Ohne sie meldete ein Vorleser „Tabelle,
                3 Zeilen" und las nackte Zellen — und auch sehend war
                `html │ 31.00 € │ nicht auf der Seite gefunden` ein
                scheinbarer Widerspruch, solange nicht danebensteht, dass Spalte 2
                „was die Technik gelesen hat" ist und Spalte 3 „ob diese Zahl im
                Seitenquelltext wiederzufinden ist". Vom Regel-16-Gate am
                gerenderten DOM gemessen (kein `th`, keine `caption`, kein
                `aria-label`). */}
            <Box component="thead">
              <Box component="tr" sx={{ '& th': {
                textAlign: 'left', pr: 2, pb: 0.5, fontWeight: 600,
                color: 'text.secondary', fontSize: FS.sm } }}>
                <th scope="col">Technique</th>
                <th scope="col">Read price</th>
                <th scope="col">Evidence on the page</th>
              </Box>
            </Box>
            <Box component="tbody" sx={{ fontSize: FS.sm }}>
              {d.candidates.map((c) => (
                <Box component="tr" key={c.technique}>
                  <Box component="td" sx={{ pr: 2, color: 'text.secondary' }}>
                    {c.technique}
                  </Box>
                  <Box component="td" sx={{ pr: 2 }}>
                    {c.price == null ? (c.error || '—') : preis(c.price)}
                  </Box>
                  <Box component="td" sx={{ color: 'text.secondary' }}>
                    {/* Drei Zustaende, nicht zwei — „nicht geprueft" ist eine
                        andere Auskunft als „nicht gefunden". Die leere Zelle
                        liess das offen. */}
                    {c.evidence === true ? 'proven'
                      : c.evidence === false ? 'not found'
                        : 'not checked'}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link href={befund.product_url} target="_blank" rel="noopener noreferrer"
                variant="caption">
            Open page
          </Link>
          <Box sx={{ flexGrow: 1 }} />
          {befund.state === 'open' ? (
            <>
              <Button size="small" disabled={busy}
                      onClick={() => onZustand(befund.id, 'dismissed')}>
                Dismiss
              </Button>
              <Button size="small" variant="outlined" disabled={busy}
                      onClick={() => onZustand(befund.id, 'done')}>
                Done
              </Button>
            </>
          ) : (
            <Button size="small" disabled={busy}
                    onClick={() => onZustand(befund.id, 'open')}>
              Reopen
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function WatchFindings() {
  const navigate = useNavigate();
  const [zustand, setZustand] = useState('open');
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const laden = useCallback(() => {
    setFehler(null);
    listWatchFindings({ state: zustand })
      .then(setDaten)
      .catch((e) => {
        // Karten des vorigen Reiters wegräumen — sie stünden sonst unter der
        // Fehlermeldung mit lebenden Knöpfen.
        setDaten(null);
        setFehler({
          message: e?.response?.data?.error
            || 'The findings could not be loaded.',
          detail: e?.message || String(e),
        });
      });
  }, [zustand]);

  useEffect(() => { laden(); }, [laden]);

  const setzeZustand = async (id, neu) => {
    setBusyId(id);
    try {
      await setWatchFindingState(id, neu);
      laden();
    } catch (e) {
      setFehler({
        message: e?.response?.data?.error || 'The state could not be set.',
        detail: e?.message || String(e),
      });
    } finally {
      setBusyId(null);
    }
  };

  const befunde = daten?.findings || [];

  return (
    <PageContainer sx={{ pb: 6 }}>
      <ViewHeader
        title="Price Checker"
        subtitle="What the nightly run found — it changes nothing, it reports"
        backLink={{ label: 'Scraper', href: '/scrapers' }}
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Scraper', href: '/scrapers' },
          { label: 'Price Checker' },
        ]}
      />

      {/* 🔴 ÜBER der Liste, und auch bei null Befunden. */}
      {daten && <LaufKarte lauf={daten.last_run} />}

      {/* E5b: was die Befunde BEDEUTEN, vor den Befunden selbst. */}
      {daten && zustand === 'open' && (
        <Diagnose diagnose={daten.diagnose}
                  onGenerator={(cid) => navigate(`/competitors/${cid}`)} />
      )}

      <Tabs value={zustand} onChange={(_, v) => setZustand(v)} sx={{ mb: SP.section }}>
        {ZUSTAENDE.map((z) => (
          <Tab key={z} value={z} label={ZUSTAND_LABEL[z]} />
        ))}
      </Tabs>

      {fehler && (
        <Alert severity="error" sx={{ mb: SP.section }}
               action={<Button color="inherit" size="small" onClick={laden}>Try again</Button>}>
          {fehler.message}
          {fehler.detail && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
              {fehler.detail}
            </Typography>
          )}
        </Alert>
      )}

      {!daten && !fehler && (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={280} />
          ))}
        </Stack>
      )}

      {daten && befunde.length === 0 && (
        <Card sx={PANEL_SX}>
          <CardContent>
            {/* Der Zustands-Reiter filtert (no-match). Ein Fehler zeigt den
                Alert oben, nicht diese Karte — der `catch` setzt `daten` auf
                null.
                🔴 Der zweite Satz ist der, der diese Liste von jeder anderen
                unterscheidet: er bleibt deshalb im `message` und wandert nicht
                in die kleinere `detail`-Zeile. */}
            <EmptyState
              reason="no-match"
              message={`No findings in this state — within the window of the last ${daten.query?.days ?? 7} days. That only means “all good” if the run above actually happened.`}
            />
          </CardContent>
        </Card>
      )}

      {/* 🔴 Stabile Live-Region: sie wird NICHT mit ihrem Inhalt eingehaengt.
          Nach einem Klick auf „Erledigt" verschwand die Karte, die Liste lud
          neu — und es wurde NICHTS angesagt. Vom Regel-16-Gate gemessen (Fokus
          fiel auf `body`, keine Live-Region aenderte ihren Text).
          Kein Toast: `grep -rn "notistack|Snackbar" frontend/src` findet null
          Treffer — ein Muster, das es nicht gibt, ist keines, auf das man
          verweisen darf.
          Nachbesserung (R16 N2, Wiedervorlage 1): der Zaehler pluralisiert —
          „1 open finding" statt des aus der deutschen Fassung mitgeschleppten
          „1 open findings". */}
      <Box component="span" role="status" aria-live="polite" sx={{
        position: 'absolute', width: '1px', height: '1px', overflow: 'hidden',
        clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
      }}>
        {daten
          ? `${befunde.length} ${ZUSTAND_LABEL[zustand].toLowerCase()} ${befunde.length === 1 ? 'finding' : 'findings'}`
          : ''}
      </Box>

      {befunde.map((b) => (
        <BefundKarte key={b.id} befund={b} busy={busyId === b.id} onZustand={setzeZustand} />
      ))}

      {befunde.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
          A finding is created <strong>anew each night</strong>. “Done” applies to the
          day, not the cause — an unfixed cause appears here seven times after a
          week. A full list therefore does not mean the checker
          is broken, but that something has been open for days.
        </Typography>
      )}
    </PageContainer>
  );
}
